import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rutasAPI } from '../../lib/apiRoutes';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const AdminRutaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompletarModal, setShowCompletarModal] = useState(false);
  const [procesando, setProcesando] = useState(false);
  
  // State for completing route with exceptions
  const [ticketsNoRealizados, setTicketsNoRealizados] = useState([]);
  const [motivos, setMotivos] = useState({});
  const [nuevasFechas, setNuevasFechas] = useState({});

  useEffect(() => {
    loadRuta();
  }, [id]);

  const loadRuta = async () => {
    setLoading(true);
    try {
      const response = await rutasAPI.getById(id);
      setRuta(response.data);
    } catch (error) {
      toast.error('Error cargando la ruta');
      console.error('Error loading route:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarRuta = async () => {
    setProcesando(true);
    try {
      await rutasAPI.changeState(id, { nuevo_estado: 'En Proceso' });
      toast.success('Ruta iniciada correctamente');
      loadRuta();
    } catch (error) {
      toast.error('Error al iniciar la ruta');
    } finally {
      setProcesando(false);
    }
  };

  const toggleTicketNoRealizado = (ticketId) => {
    setTicketsNoRealizados(prev => {
      if (prev.includes(ticketId)) {
        // Remove from list and clear motivo/fecha
        const newMotivos = { ...motivos };
        const newFechas = { ...nuevasFechas };
        delete newMotivos[ticketId];
        delete newFechas[ticketId];
        setMotivos(newMotivos);
        setNuevasFechas(newFechas);
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  };

  const handleCompletarRuta = async () => {
    setProcesando(true);
    try {
      // Validate that all non-completed tickets have a reason
      for (const ticketId of ticketsNoRealizados) {
        if (!motivos[ticketId]) {
          toast.error(`Debe indicar el motivo para el ticket ${ticketId}`);
          setProcesando(false);
          return;
        }
      }

      await rutasAPI.complete(id, {
        tickets_no_realizados: ticketsNoRealizados,
        motivos: motivos,
        nuevas_fechas: nuevasFechas
      });
      
      toast.success('Ruta completada exitosamente');
      setShowCompletarModal(false);
      loadRuta();
    } catch (error) {
      toast.error('Error al completar la ruta');
      console.error('Error completing route:', error);
    } finally {
      setProcesando(false);
    }
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado) {
      case 'Planificada':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'En Proceso':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Completada':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⚙️</div>
          <p className="text-gray-500">Cargando ruta...</p>
        </div>
      </div>
    );
  }

  if (!ruta) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-gray-700">Ruta no encontrada</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/rutas')}>
          ← Volver a rutas
        </Button>
      </div>
    );
  }

  const ticketsDetalle = ruta.tickets_detalle || [];

  return (
    <div data-testid="admin-ruta-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/rutas')}>
            ← Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {ruta.optimizada_ia && <span className="text-purple-600">🤖</span>}
              Ruta del {new Date(ruta.fecha).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </h1>
            <Badge className={`mt-1 ${getEstadoBadgeClass(ruta.estado)}`}>
              {ruta.estado}
            </Badge>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => rutasAPI.exportPDF(id)}>
            📄 Descargar PDF
          </Button>
          
          {ruta.estado === 'Planificada' && (
            <Button 
              onClick={handleIniciarRuta}
              disabled={procesando}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {procesando ? '⏳ Procesando...' : '▶️ Iniciar Ruta'}
            </Button>
          )}
          
          {ruta.estado === 'En Proceso' && (
            <Button 
              onClick={() => setShowCompletarModal(true)}
              disabled={procesando}
              className="bg-green-600 hover:bg-green-700"
            >
              ✅ Completar Ruta
            </Button>
          )}
        </div>
      </div>

      {/* Route Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Vehículo</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{ruta.vehiculo_info?.matricula || 'N/A'}</p>
            <p className="text-sm text-gray-600">{ruta.vehiculo_info?.familia} {ruta.vehiculo_info?.subfamilia}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Conductor</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{ruta.conductor || 'No asignado'}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Recogidas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{ticketsDetalle.length}</p>
            {ruta.optimization_result && (
              <p className="text-sm text-gray-600">
                ~{ruta.optimization_result.tiempo_estimado_minutos || 0} min / 
                ~{ruta.optimization_result.distancia_estimada_km?.toFixed(1) || 0} km
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Optimization Info */}
      {ruta.optimization_result?.razonamiento && (
        <Card className="mb-6 bg-purple-50 border-purple-200">
          <CardHeader>
            <CardTitle className="text-purple-800 flex items-center gap-2">
              🤖 Análisis de la IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-purple-900">{ruta.optimization_result.razonamiento}</p>
          </CardContent>
        </Card>
      )}

      {/* Completed Route Summary */}
      {ruta.estado === 'Completada' && (
        <Card className="mb-6 bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-green-800">✅ Resumen de Ejecución</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-green-700">Tickets Realizados:</p>
                <p className="text-2xl font-bold text-green-800">{ruta.tickets_realizados?.length || ticketsDetalle.length}</p>
              </div>
              <div>
                <p className="text-sm text-red-700">Tickets No Realizados:</p>
                <p className="text-2xl font-bold text-red-800">{ruta.tickets_no_realizados?.length || 0}</p>
              </div>
            </div>
            {ruta.fecha_completada && (
              <p className="text-sm text-gray-600 mt-2">
                Completada el {new Date(ruta.fecha_completada).toLocaleString('es-ES')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Secuencia de Recogidas</CardTitle>
          <CardDescription>
            {ruta.optimizada_ia ? 'Orden optimizado por IA' : 'Orden manual'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {ticketsDetalle.map((ticket, idx) => {
              const isNoRealizado = ruta.tickets_no_realizados?.includes(ticket.ticket_id);
              return (
                <div 
                  key={ticket.ticket_id} 
                  className={`p-4 flex items-start gap-4 ${isNoRealizado ? 'bg-red-50' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                    isNoRealizado 
                      ? 'bg-red-200 text-red-700' 
                      : ruta.estado === 'Completada'
                        ? 'bg-green-200 text-green-700'
                        : 'bg-blue-100 text-blue-600'
                  }`}>
                    {idx + 1}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{ticket.ticket_id}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-700">{ticket.nombre_ciudadano}</span>
                      {isNoRealizado && (
                        <Badge variant="destructive" className="ml-2">No realizado</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{ticket.direccion}</p>
                    <p className="text-sm text-gray-500">{ticket.lugar}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <span>📱 {ticket.telefono}</span>
                    </div>
                    {/* Items detail */}
                    {ticket.items && ticket.items.length > 0 && (
                      <div className="mt-2 bg-gray-50 rounded-lg p-2">
                        <div className="text-xs font-semibold text-gray-600 mb-1">📦 Enseres a recoger:</div>
                        <div className="space-y-1">
                          {ticket.items.map((item, i) => (
                            <div key={i} className="text-sm">
                              <span className="font-medium text-blue-700">{item.tipo || item.tipo_enser || 'Item'}</span>
                              {(item.descripcion || item.datos_item) && (
                                <span className="text-gray-600 ml-1">- {item.descripcion || item.datos_item}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {ticket.coordenadas?.lat && (
                    <a
                      href={`https://www.google.com/maps?q=${ticket.coordenadas.lat},${ticket.coordenadas.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      📍 Ver en mapa
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Complete Route Modal */}
      <Dialog open={showCompletarModal} onOpenChange={setShowCompletarModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✅ Completar Ruta</DialogTitle>
            <DialogDescription>
              Marque los tickets que NO se pudieron recoger. El resto se marcará como "Realizada".
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ Por defecto, todos los tickets se marcarán como completados. 
                Solo desmarque aquellos que no se pudieron recoger.
              </p>
            </div>
            
            <div className="divide-y border rounded-lg">
              {ticketsDetalle.map((ticket) => {
                const isNoRealizado = ticketsNoRealizados.includes(ticket.ticket_id);
                return (
                  <div key={ticket.ticket_id} className={`p-4 ${isNoRealizado ? 'bg-red-50' : ''}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`check-${ticket.ticket_id}`}
                        checked={!isNoRealizado}
                        onCheckedChange={() => toggleTicketNoRealizado(ticket.ticket_id)}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`check-${ticket.ticket_id}`}
                          className="font-medium cursor-pointer"
                        >
                          {ticket.ticket_id} - {ticket.nombre_ciudadano}
                        </label>
                        <p className="text-sm text-gray-600">{ticket.direccion}</p>
                      </div>
                    </div>
                    
                    {isNoRealizado && (
                      <div className="mt-3 ml-7 space-y-3 bg-red-100 p-3 rounded">
                        <div>
                          <Label className="text-red-800">Motivo de no recogida *</Label>
                          <select
                            value={motivos[ticket.ticket_id] || ''}
                            onChange={(e) => setMotivos({ ...motivos, [ticket.ticket_id]: e.target.value })}
                            className="w-full mt-1 p-2 border rounded text-sm"
                          >
                            <option value="">Seleccione un motivo...</option>
                            <option value="Ciudadano ausente">Ciudadano ausente</option>
                            <option value="Dirección incorrecta">Dirección incorrecta</option>
                            <option value="Enseres no preparados">Enseres no preparados</option>
                            <option value="Acceso imposible">Acceso imposible al lugar</option>
                            <option value="Capacidad vehículo">Capacidad del vehículo superada</option>
                            <option value="Otro">Otro motivo</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-red-800">Nueva fecha de recogida (opcional)</Label>
                          <Input
                            type="date"
                            value={nuevasFechas[ticket.ticket_id] || ''}
                            onChange={(e) => setNuevasFechas({ ...nuevasFechas, [ticket.ticket_id]: e.target.value })}
                            min={new Date().toISOString().split('T')[0]}
                            className="mt-1"
                          />
                          <p className="text-xs text-red-700 mt-1">
                            Si indica nueva fecha, el ticket volverá a estado "Pendiente"
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Resumen</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-700">✅ Tickets a completar:</span>
                  <span className="font-bold ml-2">{ticketsDetalle.length - ticketsNoRealizados.length}</span>
                </div>
                <div>
                  <span className="text-red-700">❌ Tickets no realizados:</span>
                  <span className="font-bold ml-2">{ticketsNoRealizados.length}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletarModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCompletarRuta}
              disabled={procesando}
              className="bg-green-600 hover:bg-green-700"
            >
              {procesando ? '⏳ Procesando...' : '✅ Confirmar y Completar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRutaDetail;
